import { useEffect, useRef, useState } from 'react';
import type { CreativeDeliverable } from '../../../services/api';
import { MarkdownText } from './creativeUi';
import { MediaOverlay } from './MediaOverlay';

// Visualizador de peça (carrossel/post/story): navega pelos slides como no feed, mostra a
// checagem automática de cada um, a revisão do diretor e baixa os PNGs.

const KIND_LABEL: Record<string, string> = { carousel: 'Carrossel', post: 'Post', story: 'Story', reel: 'Reel' };

function IssueBadges({ issues }: { issues?: { severity: string }[] }) {
  const errors = (issues || []).filter(i => i.severity === 'error').length;
  const warnings = (issues || []).length - errors;
  if (!errors && !warnings) return <span className="rounded bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold text-white">OK</span>;
  return (
    <span className="flex gap-0.5">
      {errors > 0 && <span className="rounded bg-rose-500 px-1.5 py-0.5 text-[9px] font-bold text-white">{errors} erro{errors > 1 ? 's' : ''}</span>}
      {warnings > 0 && <span className="rounded bg-amber-400 px-1.5 py-0.5 text-[9px] font-bold text-amber-950">{warnings}</span>}
    </span>
  );
}

// Tira compacta de slides (linha do tempo e painel).
export function SlideStrip({ slides, coverIndex, onOpen }: { slides: { url?: string; issues?: any[] }[]; coverIndex?: number; onOpen: (index: number) => void }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {slides.map((s, i) => (
        <button key={`${i}-${s.url}`} type="button" onClick={() => onOpen(i)} className="relative w-24 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {s.url && (s as any).type === 'video'
            ? <video src={`${s.url}#t=0.1`} muted playsInline preload="metadata" className="aspect-[4/5] w-full bg-black object-cover" />
            : s.url ? <img src={s.url} alt={`Slide ${i + 1}`} loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover" /> : <div className="aspect-[4/5]" />}
          <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[9px] font-bold text-white">{i + 1}{coverIndex === i ? ' · capa' : ''}</span>
          <span className="absolute bottom-1 right-1"><IssueBadges issues={s.issues} /></span>
        </button>
      ))}
    </div>
  );
}

export function DeliverableViewer({ deliverable, startIndex = 0, onClose }: { deliverable: CreativeDeliverable; startIndex?: number; onClose: () => void }) {
  const assets = (deliverable.assets || []).filter(a => a.url);
  const [i, setI] = useState(Math.min(startIndex, Math.max(0, assets.length - 1)));
  const current = assets[i];
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setI(x => Math.min(assets.length - 1, x + 1));
      if (e.key === 'ArrowLeft') setI(x => Math.max(0, x - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [assets.length]);
  // Arrastar para o lado troca de slide no celular.
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => { touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 50) return;
    setI(x => (dx < 0 ? Math.min(assets.length - 1, x + 1) : Math.max(0, x - 1)));
  };

  const download = (url: string, n: number) => {
    const a = document.createElement('a');
    a.href = url;
    const ext = (url.split('?')[0].match(/\.(\w+)$/)?.[1] || 'png').toLowerCase();
    a.download = `${deliverable.title.replace(/[^\w\- ]+/g, '').trim() || 'peca'}-${String(n + 1).padStart(2, '0')}.${ext}`;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <MediaOverlay onClose={onClose} label={deliverable.title}>
      {/* Celular: tela inteira, mídia em cima (altura flexível) e painel rolável embaixo.
          Desktop: cartão com a mídia ao lado do painel. */}
      <div className="flex h-full w-full flex-col overflow-hidden bg-white lg:h-auto lg:max-h-[92vh] lg:max-w-5xl lg:flex-row lg:rounded-2xl lg:shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="relative flex min-h-0 flex-1 items-center justify-center bg-slate-900 p-3 lg:p-4" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {current?.url && ((current as any).type === 'video'
            ? <video key={current.url} src={current.url} controls playsInline preload="metadata" className="max-h-full w-auto max-w-full rounded-lg bg-black shadow-lg lg:max-h-[78vh]" />
            : <img src={current.url} alt={`Slide ${i + 1}`} decoding="async" className="max-h-full w-auto max-w-full rounded-lg object-contain shadow-lg lg:max-h-[78vh]" />)}
          {i > 0 && <button type="button" onClick={() => setI(i - 1)} className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-lg font-bold text-slate-800 shadow" aria-label="Anterior">‹</button>}
          {i < assets.length - 1 && <button type="button" onClick={() => setI(i + 1)} className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-lg font-bold text-slate-800 shadow" aria-label="Próximo">›</button>}
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
            {assets.map((_, k) => <button key={k} type="button" onClick={() => setI(k)} className={`h-1.5 rounded-full transition-all ${k === i ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} aria-label={`Slide ${k + 1}`} />)}
          </div>
        </div>
        <aside className="max-h-[38vh] w-full shrink-0 overflow-y-auto overscroll-contain border-t border-slate-200 p-4 lg:max-h-none lg:w-80 lg:border-l lg:border-t-0 lg:p-5" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          <div className="pr-12 lg:pr-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-500">{KIND_LABEL[deliverable.kind] || deliverable.kind} · {deliverable.size}</p>
            <p className="text-base font-bold text-slate-900">{deliverable.title}</p>
            <p className="text-xs text-slate-500">Slide {i + 1} de {assets.length}{deliverable.coverIndex === i ? ' · capa' : ''}</p>
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Checagem automática</p>
            {current?.issues?.length ? (
              <ul className="mt-2 space-y-1.5">
                {current.issues.map((iss, k) => (
                  <li key={k} className={`rounded-lg px-2.5 py-1.5 text-xs ${iss.severity === 'error' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-800'}`}>{iss.message}</li>
                ))}
              </ul>
            ) : <p className="mt-2 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">Nenhum problema neste slide.</p>}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {current?.url && <button type="button" onClick={() => download(current.url!, i)} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">Baixar este arquivo</button>}
            {assets.length > 1 && <button type="button" onClick={() => assets.forEach((a, k) => setTimeout(() => download(a.url!, k), k * 350))} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Baixar todos</button>}
          </div>

          {deliverable.review && (
            <div className="mt-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Revisão do diretor</p>
              <MarkdownText text={deliverable.review} className="mt-1 text-xs text-slate-600" />
            </div>
          )}
        </aside>
      </div>
    </MediaOverlay>
  );
}
